import { ConstructionOutlined } from '@mui/icons-material';
import React from 'react';


function useCategoryDataManager() {
  const categories = React.useRef([]);

  const slugs = React.useRef([]);

  const saveSlugs = () => {
    slugs.current = getIncludedSlugs();
  }

  const categoriesInitialized = (cats) => {
    let tf = cats
      ? cats.sort((a, b) => {
        return a.sort > b.sort ? 1 : -1;
      })
      : [];
  
    tf = tf.map((item) => {
      item.filters = [0,5];
      item.exclude = false;
      return item;
    });
    return tf;
  }

  React.useEffect(() => {
    saveSlugs();
  },[categories.current]);

  const toggleIncludeCategory = (id) => {
    if (!categories.current || categories.current.length === 0) {
      return;
    }
    categories.current = categories.current.map((cat) => { 
      if (cat.id === id) {
        const exc = !cat.exclude;
        return { ...cat, exclude: exc }
      }
      return cat;
    });
  }

  const getIncludedSlugs = () => {
    if (!categories.current || categories.current.length === 0) {
      return ['Pp', 'Dw', 'Cult', 'Infra', 'Wl', 'Whp'];
    }
    return categories.current.filter((item) => !item.exclude).map((item) => item.slug);
  }

  const fetchJenksScoreForCategory = (slug, properties) => {
    let score = 0;
    switch (slug) {
      case 'Pp':
        score = properties.jenks_pp;
        break;
      case 'Infra':
        score = properties.jenks_infra;
        break;
      case 'Dw':
        score = properties.jenks_dw;
        break;
      case 'Wl':
        score = properties.jenks_wildlife;
        break;
      case 'Cult':
        score = properties.jenks_cult;
        break;
      case 'Whp':
        score = properties.jenks_whp;
        break;
      default:
        console.error(`Invalid slug value encountered in filters ${slug}.  Only valid values are: Pp, Infra, Dw, Wl, Cult, Whp`); // eslint-disable-line no-console 
        break;
    }
    return score;
  }

  const getFilters = () => {
    let ppRange = [0,5];
    let infraRange = [0,5];
    let dwRange = [0,5];
    let wlRange = [0,5];
    let cultRange = [0,5];
    let whpRange = [0,5];

    categories.current.map(({slug, filters, exclude}) => {
      if (exclude) return;
  
      switch (slug) {
        case 'Pp':
          ppRange = filters;
          break;
        case 'Infra':
          infraRange = filters;
          break;
        case 'Dw':
          dwRange = filters;
          break;
        case 'Wl':
          wlRange = filters;
          break;
        case 'Cult':
          cultRange = filters;
          break;
        case 'Whp':
          whpRange = filters;
          break;
        default:
          console.error(`Invalid slug value encountered in filters ${slug}.  Only valid values are: Pp, Infra, Dw, Wl, Cult, Whp`); // eslint-disable-line no-console 
          break;
      }
    });
    return { ppRange, infraRange, dwRange, wlRange, cultRange, whpRange };
  }

  const convertAndRoundIfNecessary = ({scale = 1, precision=0} = {}, value) => {
    if (!scale && !precision) {
      return value;
    }
    let convertedValue = value;
    if (isNaN(scale) === false) {
      convertedValue = value * +scale;
    }
    if (isNaN(precision) === false) {
      convertedValue = convertedValue.toFixed(+precision);
    }

    return convertedValue;
  }
  const computeHudenAvgBucket = (value) => {
    if (value <= 0) {
      return 0;
    }
    const classificationRange = [
      [0.000001, 6.170000, 1], 
      [6.170000, 12.350000, 2],
      [12.350000, 24.710000, 3], 
      [24.710000, 49.420000, 4], 
      [49.420000, 123.550000, 5],
      [123.550000, 741.310000, 6],
      [741.310000, 100000, 7]
    ];
    let classification = 0;
    classificationRange.forEach((range) => {
      if (value >= range[0] && value < range[1]) {
        classification = range[2];
      }
    });
    return classification;
  }

  const transformHudenBucketToString = (bucket, value) => {
    const suffix = `(average of ${value} HU / acre)`;
    switch (bucket) {
      case 1:
        return `Below Density Rating ${suffix}`;
      case 2:
        return `Very Low ${suffix}`;
      case 3:
        return `Low ${suffix}`;
      case 4:
        return `Medium ${suffix}`;
      case 5:
        return `Medium-High ${suffix}`;
      case 6:
        return `High ${suffix}`;
      case 7:
        return `Very High ${suffix}`;
      default:
        return 'No Housing Units';
    }
  }

  const transFormIdentifyPropertyIfNeccessary = ({transform} = {}, value) => {
    if (!transform) {
      return value;
    }
    let res = '';
    if (transform === 'huden_avg_transform') {
      const formattedValue = convertAndRoundIfNecessary({scale: 1, precision: 2}, value);
      const bucket = computeHudenAvgBucket(formattedValue);
      res = transformHudenBucketToString(bucket, formattedValue);
    }
    return res;
  }

  const buildLinesForCategory = (category, feature) => {
    const res = [];
    if (!category || !feature) {
      return res;
    }
    if (category.identificationMetrics?.length > 0) {
      category.identificationMetrics.forEach((metric, index) => {
        let theValue = convertAndRoundIfNecessary(metric, feature.properties[metric.field]);
        theValue = transFormIdentifyPropertyIfNeccessary(metric, theValue);
        const line = {
          id: `${category.id}-${index}`,
          label: metric.label,
          value: theValue,
          units: metric.units
        };
        res.push(line);
      });
    }
    return res;
  };

  // The inspector tool has a special featured card section that we want our
  // whp category to be in.  This function will build the lines for that card.
  // and also extract the whp category and populate the featured card.
  const buildUpdatedCategoriesForInspector = (feature, mapLegend) => {
    let cardData = [];
    const jenksThreat =  feature?.properties?.jenksThreat || 0;
    const featureCard = mapLegend.current.find((item) => item.score === jenksThreat?.toString());

    if (categories.current.length > 0) {
      categories.current.forEach((cat) => {
        const score = fetchJenksScoreForCategory(cat.slug, feature?.properties);
        const theFeatLegend = mapLegend.current.find((item) => item.score === score?.toString());
        const card = {
          heading: cat.label,
          legendLabel: theFeatLegend?.legendLabel,
          legendColor: theFeatLegend?.legendColor,
          toolTip: cat.toolTip,
          footerLabel: null,
          footerLink: null,
          lines: buildLinesForCategory(cat, feature)
        };
        cardData.push(card);
      });
    }
    return {cardData, featureCard};
  }
  
  const buildUpdatedInspectorData = (feature, mapLegend, theme, identifyHeading, featuredCardHeading) => {
    const {cardData, featureCard} = buildUpdatedCategoriesForInspector(feature, mapLegend);

    const updatedFeat = {
      theme,
      cardData,
      heading: identifyHeading,
      featuredCard: {
        heading: featuredCardHeading,
        legendLabel: featureCard?.legendLabel,
        legendColor: featureCard?.legendColor,
      }
    };

    return updatedFeat;
  }

  return {
    categories,
    slugs,
    saveSlugs,
    categoriesInitialized,
    getIncludedSlugs,
    getFilters,
    toggleIncludeCategory,
    buildUpdatedInspectorData
  };
}

export default useCategoryDataManager;